import React, { useEffect } from 'react';
import { useWindowSize } from 'react-use';
import { Breakpoint, theme } from '@noths/polaris-client-ribbons-base';
import { DrawerHeader, Media } from '@noths/polaris-client-ribbons-design-system';
import { pxToRem } from '@noths/polaris-client-styles';
import { SerializedError } from '@reduxjs/toolkit';
import { NextRouter, useRouter } from 'next/router';

import * as copy from '../constants/copy';
import { DESKTOP_FILTER_HEIGHT_CSS_VAR } from '../constants/cssVariables';
import { FilterWithFilterOptionsCount } from '../modules/selectors';
import { CollapsibleFilterListItem } from './CollapsibleFilterListItem';
import { FilterControls } from './FilterControls';
import { FilterListItem } from './FilterListItem';
import { ListingsApiError } from './ListingsApiError';
import * as styles from '../styles/FilterList.styles';
import * as modalStyles from '../styles/FilterModal.styles';

interface Props {
  isLoading: boolean;
  filters: FilterWithFilterOptionsCount[];
  onCloseFilters: VoidFunction;
  onViewResults: VoidFunction;
  onFilterListItemVisibility: (filterUid: string) => void;
  totalSelectedFilterOptionsCount: number;
  totalProducts: number;
  onResetFilters: (router: NextRouter) => void;
  isUsingExpandableFilters: boolean;
  error?: SerializedError | null;
}

const setFilterHeight = (windowHeight: number) =>
  document.documentElement.style.setProperty(
    DESKTOP_FILTER_HEIGHT_CSS_VAR,
    `calc(${windowHeight}px - ${pxToRem(theme.spacing.xl)})`,
  );

const NUMBER_OF_DEFAULT_EXPANDED_COLLAPSIBLE_FILTERS = 2;

export const FilterList = ({
  isLoading,
  filters,
  onCloseFilters,
  onViewResults,
  onResetFilters,
  onFilterListItemVisibility,
  totalSelectedFilterOptionsCount,
  totalProducts,
  error,
  isUsingExpandableFilters,
}: Props) => {
  const router = useRouter();
  const { height } = useWindowSize();

  useEffect(() => {
    setFilterHeight(height);
  }, [height]);

  return (
    <form
      css={[modalStyles.modalViewport, styles.filterListWrapper]}
      onSubmit={(e) => e.preventDefault()}
    >
      <Media lessThan={Breakpoint.L}>
        <DrawerHeader
          headingLevel="h2"
          title={copy.FILTER_MODAL_HEADING}
          onClose={onCloseFilters}
        />
      </Media>
      {error ? (
        <ListingsApiError />
      ) : (
        // this shows for everything, not just Desktop
        <ul css={[styles.list, modalStyles.modalScrollableContent]}>
          {filters.map((filter, index) => {
            if (filter.options.length === 0) {
              return null;
            }

            if (isUsingExpandableFilters) {
              return (
                <CollapsibleFilterListItem
                  filter={filter}
                  key={filter.uid}
                  isExpanded={index + 1 <= NUMBER_OF_DEFAULT_EXPANDED_COLLAPSIBLE_FILTERS}
                  onFilterListItemVisibility={onFilterListItemVisibility}
                />
              );
            }

            return (
              <FilterListItem
                filter={filter}
                key={filter.uid}
                onFilterListItemVisibility={onFilterListItemVisibility}
              />
            );
          })}
        </ul>
      )}
      <Media lessThan={Breakpoint.L}>
        <FilterControls
          isLoading={isLoading}
          totalProducts={totalProducts}
          totalSelectedFilterOptionsCount={totalSelectedFilterOptionsCount}
          onViewResults={onViewResults}
          resetText={copy.FILTER_LIST_RESET}
          error={error}
          onReset={() => onResetFilters(router)}
        />
      </Media>
    </form>
  );
};
